import app from '@/app.js';
import router from '@/router.js';
import { component } from 'picoapp';
import { gsap } from 'gsap';
import { addItemById } from '@/lib/cart';
import { formatMoney } from '@/lib/currency.js';

export default component((node, ctx) => {
  const isMobile = window.matchMedia('(max-width: 600px)');
  const isDesktop = window.matchMedia('(min-width: 1200px)');
  const fieldsetTemplate = document.getElementById('quickAddFieldset');
  const optionTemplate = document.getElementById('quickAddOption');
  const variantsContainer = node.querySelector('.js-quick-add-variants');
  const addToBasketButton = node.querySelector('.js-add-to-basket');

  let selectedOptions = {};
  let availableCombinations = null;

  const populate = (ctx) => {
    const { quickAddProduct: product } = ctx;
    const variants = document.createElement('div');

    product.options.forEach((option, index) => {
      if (option.name == 'Title') return; // Ignore the default Shopify variant that you can't delete 
      
      // Remove unavailable options (out of stock)
      product.variants.forEach(variant => {
        if(variant.available == false) {
          if(option.values.includes(variant.title)) {
            const valueToRemove = variant.title;
            option.values = option.values.filter(item => item !== valueToRemove);
          }
        }
      })

      variants.appendChild(createFieldset(product, option, index));
    });

    variantsContainer.replaceChildren(variants);

    // Reset quantity input
    const quantity = node.querySelector('.js-quantity-input');
    quantity.value = 1;

    // Reset available combinations + selectedOptions
    selectedOptions = {};
    availableCombinations = null;

    app.unmount();
    app.mount();
    updateButtonState();
  };

  const show = (ctx) => {
    const { quickAddTarget: target, quickAddFit: fit } = ctx;
    const { left, bottom } = target.getBoundingClientRect();

    if (!isDesktop.matches) {
      document.body.classList.add('overflow-hidden');
      gsap.set(node, { width: '100%', top: 'auto', bottom: 0, left: 0 });

    } else {
      gsap.set(node, {
        width: `${fit ? target.offsetWidth : 400}px`,
        top: (bottom + window.scrollY) + 16,
        bottom: 'auto',
        left,
      });

      document.addEventListener('click', handleClickOutside);
    }

    gsap.to(node, { y: 0, autoAlpha: 1, duration: 0.2, overwrite: true });
  };

  const handleClickOutside = (e) => {
    const button = app.getState().quickAddTarget;
    if (!e.composedPath().includes(button)
      && !e.composedPath().includes(node)) {
      hide(e);
    }
  };

  const hide = () => {
    gsap.to(node, { autoAlpha: 0, y: isMobile.matches ? '100%' : 0, duration: isMobile.matches ? 0.3 : 0 });
    document.body.classList.remove('overflow-hidden');
    document.removeEventListener('click', handleClickOutside);
  };

  const getAvailableCombinations = () => {
    const product = app.getState().quickAddProduct;
    return product.variants.filter((variant) => {
      return Object.keys(selectedOptions).every((key) => {
        return variant[key] === selectedOptions[key];
      });
    });
  };

  const resetDropdownLabel = (field) => {
    const radioLabel = field.parentElement.querySelector('.js-dropdown-selected');
    radioLabel.textContent = radioLabel.dataset.defaultLabel;
  };

  const updateRadioInputs = (field) => {
    const inputs = field.querySelectorAll('input');
    availableCombinations = getAvailableCombinations();

    inputs.forEach((input) => {
      input.checked = false; // Uncheck all.
        
      const isAvailable = availableCombinations.some((variant) => {
        return variant[`option${field.dataset.position}`] === input.value;
      });
      if (isAvailable) {
        input.removeAttribute('disabled');
        input.parentElement.classList.remove('is-disabled');
      } else {
        input.setAttribute('disabled', '');
        input.parentElement.classList.add('is-disabled');
      }
    });
  };

  function updateVariantImage(id, product, e) {

    let images = document.querySelectorAll(`[data-variant-image='${id}']`);

    // Hide all images
    let allImages = document.querySelectorAll(`[data-product-id="${product.id}"] img`);
    for (let i = 0; i < allImages.length; i++) {
      allImages[i].classList.add('hidden');
    }

    // Show selected variant
    for (let i = 0; i < images.length; i++) {
      images[i].classList.remove('hidden')
    }
  }

  const updateUI = (e) => {
    const { quickAddProduct: product } = ctx.getState();
    const { position } = e.currentTarget.dataset;
    const { value } = e.srcElement;
    const fields = node.querySelectorAll('.js-quick-add-select');

    // Add selected value to 'state' object
    selectedOptions[`option${position}`] = value;

    fields.forEach((field) => {
      // Ignore current and previous options
      if (field.dataset.position <= position) return;
      delete selectedOptions[`option${field.dataset.position}`];
      resetDropdownLabel(field);
    });
    
    fields.forEach((field) => {
      // Ignore fields with selected values
      if (selectedOptions[`option${field.dataset.position}`]) return;
      updateRadioInputs(field);
    });

    if (availableCombinations) {
      const variantsSortedByPrice = availableCombinations.sort((a, b) => {
        return a.price - b.price;
      });
  
      const productPrices = document.querySelectorAll(`[data-product-id="${product.id}"] .js-product-price`);
  
      for (let i = 0; i < productPrices.length; i++) {
        productPrices[i].innerText = formatMoney(variantsSortedByPrice[0]?.price);
      }
      
    }

    let variantId = getProductVariantId();
    if(variantId) {
      updateVariantImage(variantId, product, e);
    }

    updateButtonState();
  };

  const createFieldset = (product, option, index) => {
    const fieldset = fieldsetTemplate.content.cloneNode(true);
    fieldset.querySelector('legend').innerText = option.name;
    const select = fieldset.querySelector('.js-quick-add-select');

    option.values.forEach((value) => {
      const title  = value.replace('Â', '');
      const variant = optionTemplate.content.cloneNode(true);
      const label = variant.querySelector('label');
      const input = variant.querySelector('input');

      const uid = `${product.handle}-${option.name}-${title}`;

      label.innerText = title;
      label.setAttribute('for', uid);

      input.value = value;
      input.setAttribute('id', uid);
      input.setAttribute('name', option.name);

      select.appendChild(variant);
      select.dataset.position = index + 1;
      select.addEventListener('change', updateUI);
    });
    return fieldset;
  };

  const increaseQuantity = () => {
    const input = node.querySelector('.js-quantity-input');
    input.stepUp();
  };

  const decreaseQuantity = () => {
    const input = node.querySelector('.js-quantity-input');
    input.stepDown();
  };

  const addQuantityEventListeners = () => {
    const decrease = node.querySelector('.js-quantity-decrease');
    const increase = node.querySelector('.js-quantity-increase');
    increase.addEventListener('click', increaseQuantity);
    decrease.addEventListener('click', decreaseQuantity);
  };

  const updateButtonState = () => {
    let disabled = false;
    const radioGroups = node.querySelectorAll('[data-type="radio"]');
    const numberInputs = node.querySelectorAll('input[type="number"]');

    radioGroups.forEach((group) => {
      const checked = group.querySelector('input:checked');
      if (!checked) disabled = true;
    });

    numberInputs.forEach((input) => {
      if (input.value < 1) disabled = true;
    });

    const { quickAddProduct: product } = ctx.getState();

    console.log(getProductVariantAvailability())
    const isAvailable = getProductVariantAvailability()

    if(!isAvailable) {
      disabled = true
    }

    if (disabled) {
      addToBasketButton.setAttribute('disabled', '');
    } else {
      addToBasketButton.removeAttribute('disabled');
    }
  };

  const addToBasket = (e) => {
    e.preventDefault();
    app.emit('cart:updating');
    const id = getProductVariantId();
    const quantity = node.querySelector('.js-quantity-input').value;

    addItemById(id, quantity);
  };

  const getProductVariantId = () => {
    const product = app.getState().quickAddProduct;
    const formData = new FormData(node);
    const selectedValues = Array.from(formData.values()).join( ' / ');

    if (product.variants.length > 1) {
      const variant = product.variants.find((variant) => {
        return variant.title === selectedValues;
      });
      if(variant) return variant.id;
    } else {
      return product.variants[0].id;
    }
  };

  const getProductVariantAvailability = () => {
    const product = app.getState().quickAddProduct;
    const formData = new FormData(node);
    const selectedValues = Array.from(formData.values()).join( ' / ');

    if (product.variants.length > 1) {
      const variant = product.variants.find((variant) => {
        return variant.title === selectedValues;
      });
      if(variant) return variant.available;
    } else {
      return product.variants[0].available;
    }
  };

  const init = () => {
    const quickAddButtons = document.querySelectorAll('.js-quick-add');
    node.addEventListener('change', updateButtonState);
    ctx.on('changeQuickAddProduct', populate);
    ctx.on('showQuickAdd', show);
    ctx.on('cart:updated', hide);

    addToBasketButton.addEventListener('click', addToBasket);

    quickAddButtons.forEach((button) => {
      button.addEventListener('click', () => {

        const currentProduct = app.getState().quickAddProduct;
        const newProduct = JSON.parse(window.atob(button.dataset.product));

        // If product has changed, emit event to re-populate quickAdd menu
        if (newProduct.id !== currentProduct.id) {
          app.emit('changeQuickAddProduct', { quickAddProduct: newProduct });
        }
        app.emit('showQuickAdd', {
          quickAddTarget: button.closest(button.dataset.alignTo) || button,
          quickAddFit: button.dataset.fit || false,
        });
      });
    });

    if (!isDesktop.matches) {
      node.querySelector('.js-close-quick-add').addEventListener('click', hide);
    }

    isMobile.addEventListener('change', hide);
    app.on('recommendations:updated', init);
  };

  addQuantityEventListeners();

  init();

  app.on('quiz:results', init);

  router.on('after', () => { 
    hide();
    init();
  });

});
